import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Lock, CheckCircle, Loader2, AlertCircle, Check, X } from 'lucide-react';
import api from '@/services/api';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [tokenValid, setTokenValid] = useState(true);

    // Get token from URL params
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setTokenValid(false);
        }
    }, [token]);

    // Password requirements - simplified to match backend validation
    const passwordRequirements = [
        { regex: /.{8,}/, text: 'At least 8 characters' }
    ];

    // Optional additional requirements for better UX (not enforced by backend)
    const optionalRequirements = [
        { regex: /[A-Z]/, text: 'One uppercase letter (recommended)' },
        { regex: /[a-z]/, text: 'One lowercase letter (recommended)' },
        { regex: /[0-9]/, text: 'One number (recommended)' },
        { regex: /[!@#$%^&*]/, text: 'One special character (recommended)' }
    ];

    const checkPasswordStrength = (password) => {
        const required = passwordRequirements.map(req => ({
            ...req,
            met: req.regex.test(password),
            required: true
        }));

        const optional = optionalRequirements.map(req => ({
            ...req,
            met: req.regex.test(password),
            required: false
        }));

        return [...required, ...optional];
    };

    const resetPasswordMutation = useMutation({
        mutationFn: async (data) => {
            const response = await api.post('/auth/reset-password', data);
            return response.data;
        },
        onSuccess: () => {
            // Password reset successful
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Failed to reset password';
            if (message.includes('expired') || message.includes('invalid')) {
                setTokenValid(false);
            }
            setErrors({ general: message });
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear errors when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.newPassword) {
            newErrors.newPassword = 'Password is required';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            resetPasswordMutation.mutate({
                token,
                newPassword: formData.newPassword
            });
        }
    };

    if (!tokenValid) {
        return (
            <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center">
                            <div className="bg-red-500/15 p-3 rounded-full">
                                <AlertCircle className="h-12 w-12 text-red-400" />
                            </div>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                            Invalid or Expired Link
                        </h2>
                        <p className="mt-2 text-center text-sm text-hx-muted">
                            This password reset link is invalid or has expired. Please request a new one.
                        </p>
                    </div>

                    <div className="mt-8">
                        <Link
                            to="/forgot-password"
                            className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-hx-ink bg-hx-accent hover:bg-hx-accent2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent"
                        >
                            Request new reset link
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (resetPasswordMutation.isSuccess) {
        return (
            <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center">
                            <div className="bg-hx-ok/15 p-3 rounded-full">
                                <CheckCircle className="h-12 w-12 text-hx-ok" />
                            </div>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                            Password Reset Successful
                        </h2>
                        <p className="mt-2 text-center text-sm text-hx-muted">
                            Your password has been reset successfully. Redirecting to login...
                        </p>
                        <div className="mt-4">
                            <Loader2 className="animate-spin h-8 w-8 text-hx-accent mx-auto" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const passwordStrength = checkPasswordStrength(formData.newPassword);

    return (
        <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-hx-bg py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md bg-hx-panel border border-hx-line rounded-2xl p-6 sm:p-8 shadow-2xl w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-hx-text">
                        Reset your password
                    </h2>
                    <p className="mt-2 text-center text-sm text-hx-muted">
                        Enter your new password below
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {errors.general && (
                        <div className="rounded-md bg-red-500/10 border border-red-500/30 p-4">
                            <div className="flex">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                                <p className="ml-3 text-sm text-red-300">{errors.general}</p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-hx-muted mb-1">
                                New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                                    <Lock className={`h-5 w-5 ${errors.newPassword ? 'text-red-400' : 'text-hx-dim'}`} />
                                </div>
                                <input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    required
                                    className={`appearance-none bg-hx-bg2 relative block w-full pl-10 pr-10 px-3 py-2 border ${
                                        errors.newPassword ? 'border-red-500/60' : 'border-hx-line2'
                                    } placeholder-hx-dim text-hx-text rounded-md focus:outline-none focus:ring-hx-accent focus:border-hx-accent focus:z-10 sm:text-sm`}
                                    placeholder="Enter new password"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5 text-hx-dim hover:text-hx-muted" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-hx-dim hover:text-hx-muted" />
                                    )}
                                </button>
                            </div>
                            {errors.newPassword && (
                                <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>
                            )}
                        </div>

                        {/* Password Requirements */}
                        {formData.newPassword && (
                            <div className="mt-2 p-3 bg-hx-bg rounded-md">
                                <p className="text-xs font-medium text-hx-muted mb-2">Password requirements:</p>
                                <ul className="space-y-1">
                                    {passwordStrength.map((req, index) => (
                                        <li key={index} className="flex items-center text-xs">
                                            {req.met ? (
                                                <Check className="h-3 w-3 text-hx-ok mr-2" />
                                            ) : (
                                                <X className={`h-3 w-3 ${req.required ? 'text-red-400' : 'text-hx-dim'} mr-2`} />
                                            )}
                                            <span className={
                                                req.met
                                                    ? 'text-hx-ok'
                                                    : req.required
                                                        ? 'text-red-400 font-medium'
                                                        : 'text-hx-muted'
                                            }>
                                                {req.text}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-hx-muted mb-1">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                                    <Lock className={`h-5 w-5 ${errors.confirmPassword ? 'text-red-400' : 'text-hx-dim'}`} />
                                </div>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    required
                                    className={`appearance-none bg-hx-bg2 relative block w-full pl-10 pr-10 px-3 py-2 border ${
                                        errors.confirmPassword ? 'border-red-500/60' : 'border-hx-line2'
                                    } placeholder-hx-dim text-hx-text rounded-md focus:outline-none focus:ring-hx-accent focus:border-hx-accent focus:z-10 sm:text-sm`}
                                    placeholder="Confirm new password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-5 w-5 text-hx-dim hover:text-hx-muted" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-hx-dim hover:text-hx-muted" />
                                    )}
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={resetPasswordMutation.isPending}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-hx-ink bg-hx-accent hover:bg-hx-accent2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-hx-panel focus:ring-hx-accent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {resetPasswordMutation.isPending ? (
                                <>
                                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                    Resetting...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/login"
                            className="text-sm text-hx-accent hover:text-hx-accent2"
                        >
                            Back to login
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;